import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Contact as ContactIcon, Plus, Trash2, Pencil, Phone, Mail } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/contacts")({
  component: ContactsPage,
  head: () => ({ meta: [{ title: "Contacts — MissionReady" }] }),
});

type Contact = {
  id: string; site_id: string | null; category: string;
  name: string; role_title: string | null;
  phone: string | null; email: string | null; notes: string | null;
};
type Site = { id: string; name: string };

const CATEGORIES = [
  { value: "hr", label: "HR" },
  { value: "compliance", label: "Compliance" },
  { value: "payroll", label: "Payroll" },
  { value: "emergency", label: "Emergency" },
  { value: "site_poc", label: "Site POC" },
];

function ContactsPage() {
  const { currentOrg, currentRole, isPlatformAdmin } = useAuth();
  const canEdit = isPlatformAdmin || ["org_owner", "admin", "supervisor"].includes(currentRole ?? "");
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);
  const [form, setForm] = useState<Partial<Contact>>({ category: "hr" });
  const [catFilter, setCatFilter] = useState("all");

  const load = async () => {
    if (!currentOrg) return;
    const [{ data: c }, { data: s }] = await Promise.all([
      supabase.from("org_contacts").select("*").eq("organization_id", currentOrg.id).order("category").order("name"),
      supabase.from("sites").select("id,name").eq("organization_id", currentOrg.id).order("name"),
    ]);
    setContacts((c ?? []) as Contact[]);
    setSites((s ?? []) as Site[]);
  };
  useEffect(() => { load(); }, [currentOrg?.id]);

  const startCreate = () => { setEditing(null); setForm({ category: "hr" }); setOpen(true); };
  const startEdit = (c: Contact) => { setEditing(c); setForm(c); setOpen(true); };

  const save = async () => {
    if (!currentOrg || !form.name || !form.category) return toast.error("Name and category required");
    const payload = {
      organization_id: currentOrg.id,
      site_id: form.site_id ?? null,
      category: form.category, name: form.name,
      role_title: form.role_title ?? null,
      phone: form.phone ?? null, email: form.email ?? null,
      notes: form.notes ?? null,
    };
    const { error } = editing
      ? await supabase.from("org_contacts").update(payload).eq("id", editing.id)
      : await supabase.from("org_contacts").insert(payload);
    if (error) return toast.error(error.message);
    toast.success(editing ? "Updated" : "Added");
    setOpen(false); load();
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this contact?")) return;
    const { error } = await supabase.from("org_contacts").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Deleted"); load();
  };

  const siteName = (sid: string | null) => sid ? (sites.find((s) => s.id === sid)?.name ?? "—") : "All sites";
  const grouped = useMemo(() => {
    const list = catFilter === "all" ? contacts : contacts.filter((c) => c.category === catFilter);
    const g: Record<string, Contact[]> = {};
    list.forEach((c) => { (g[c.category] ??= []).push(c); });
    return g;
  }, [contacts, catFilter]);

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ContactIcon className="h-6 w-6 text-[#CC0000]" />
          <div>
            <h1 className="text-2xl font-bold text-white">Contacts</h1>
            <p className="text-sm text-white/60">Org directory</p>
          </div>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={startCreate} className="bg-[#CC0000] hover:bg-[#a30000] text-white">
                <Plus className="h-4 w-4 mr-1" /> Add Contact
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editing ? "Edit Contact" : "Add Contact"}</DialogTitle></DialogHeader>
              <div className="grid gap-3">
                <div>
                  <Label>Category *</Label>
                  <Select value={form.category ?? "hr"} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger aria-label="Category *"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Name *</Label><Input aria-label="Name *" value={form.name ?? ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label>Role / Title</Label><Input aria-label="Role / Title" value={form.role_title ?? ""} onChange={(e) => setForm({ ...form, role_title: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Phone</Label><Input aria-label="Phone" value={form.phone ?? ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                  <div><Label>Email</Label><Input aria-label="Email" type="email" value={form.email ?? ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                </div>
                <div>
                  <Label>Site (optional)</Label>
                  <Select value={form.site_id ?? "none"} onValueChange={(v) => setForm({ ...form, site_id: v === "none" ? null : v })}>
                    <SelectTrigger aria-label="Site (optional)"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— All sites —</SelectItem>
                      {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Notes</Label><Textarea aria-label="Notes" value={form.notes ?? ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button onClick={save} className="bg-[#CC0000] hover:bg-[#a30000]">{editing ? "Save" : "Add"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Label className="text-white/70">Category:</Label>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger aria-label="Category:" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-4">
        {Object.keys(grouped).length === 0 && <p className="text-sm text-white/50">No contacts yet.</p>}
        {Object.entries(grouped).map(([cat, list]) => (
          <div key={cat}>
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-[#CC0000]">{CATEGORIES.find((c) => c.value === cat)?.label ?? cat}</h2>
            <div className="grid gap-2">
              {list.map((c) => (
                <div key={c.id} className="flex items-start justify-between rounded-lg border border-white/10 bg-white/5 p-3">
                  <div className="min-w-0">
                    <div className="font-medium text-white">{c.name}</div>
                    <div className="text-xs text-white/50">{c.role_title ?? ""} · {siteName(c.site_id)}</div>
                    <div className="mt-1 flex flex-wrap gap-3 text-xs">
                      {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-1 text-white/80 hover:text-[#CC0000]"><Phone className="h-3 w-3" />{c.phone}</a>}
                      {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-1 text-white/80 hover:text-[#CC0000]"><Mail className="h-3 w-3" />{c.email}</a>}
                    </div>
                    {c.notes && <p className="mt-1 text-xs text-white/60">{c.notes}</p>}
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="icon" variant="ghost" onClick={() => startEdit(c)} aria-label={`Edit contact ${c.name}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(c.id)} aria-label={`Delete contact ${c.name}`}><Trash2 className="h-4 w-4 text-[#CC0000]" /></Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
